import React, { useState, useEffect } from 'react';

// Full organization interface from the API
interface ApiOrganization {
  organization_id: string;
  organization_code: string;
  name: string;
  description: string;
  created_at: string;
  updated_at: string;
}

// The first user seeded into a brand-new org (create mode only).
export interface InitialUserData {
  email: string;
  name?: string;
  role: string; // RBAC RoleKey
}

// Organization data for form submissions
interface OrganizationFormData {
  organization_id?: string;
  organization_code: string;
  name: string;
  description?: string;
  // Present only when creating a new org — the parent routes create vs. edit
  // to different endpoints (onboard vs. plain update).
  initialUser?: InitialUserData;
}

interface OrganizationFormProps {
  organization?: ApiOrganization;
  onSubmit: (organizationData: OrganizationFormData) => Promise<void>;
  onCancel: () => void;
}

// Only roles that need no team can seed a brand-new org (it has no teams yet).
// END_USER / TEAM_LEAD require a team_id, so they are excluded here — those
// users get added later, once the Organization Admin has created teams.
const INITIAL_USER_ROLES = [
  { key: 'FIRM_ADMIN', label: 'Organization Admin' },
  { key: 'VIEWER', label: 'Viewer' },
];

const OrganizationForm: React.FC<OrganizationFormProps> = ({
  organization,
  onSubmit,
  onCancel
}) => {
  const [organizationData, setOrganizationData] = useState<OrganizationFormData>({
    organization_code: '',
    name: '',
    description: ''
  });
  const [initialUser, setInitialUser] = useState<InitialUserData>({
    email: '',
    name: '',
    role: 'FIRM_ADMIN',
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const isEditMode = !!organization?.organization_id;

  // If editing, load organization data
  useEffect(() => {
    if (organization) {
      setOrganizationData({
        organization_id: organization.organization_id,
        organization_code: organization.organization_code,
        name: organization.name,
        description: organization.description || ''
      });
    }
  }, [organization]);

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const { name, value } = e.target;
    setOrganizationData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleInitialUserChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>
  ) => {
    const { name, value } = e.target;
    setInitialUser(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    try {
      // On create, carry the initial-user section so the parent can onboard the
      // org and its first user in one call. Edit path never sends it.
      const payload: OrganizationFormData = isEditMode
        ? organizationData
        : {
            ...organizationData,
            initialUser: {
              email: initialUser.email.trim(),
              name: initialUser.name?.trim() || undefined,
              role: initialUser.role,
            },
          };
      await onSubmit(payload);
    } catch (err) {
      // Surface the server's detail (e.g. "Organization code 'x' already
      // exists", "That ... email is already in use") so the admin knows what to
      // change, not a generic message. fetchWithAuth throws an Error whose
      // message carries the backend detail.
      const message = err instanceof Error && err.message ? err.message : null;
      setError(message || 'An error occurred. Please try again.');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      {error && (
        <div className="bg-red-50 p-4 rounded-lg text-red-800 mb-4">
          {error}
        </div>
      )}

      <div>
        <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-1">
          Organization Name *
        </label>
        <input
          type="text"
          id="name"
          name="name"
          value={organizationData.name}
          onChange={handleChange}
          className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-primary/50 focus:border-primary"
          required
        />
      </div>

      <div>
        <label htmlFor="organization_code" className="block text-sm font-medium text-gray-700 mb-1">
          Organization Code * {isEditMode && '(Cannot be changed)'}
        </label>
        <input
          type="text"
          id="organization_code"
          name="organization_code"
          value={organizationData.organization_code}
          onChange={handleChange}
          className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-primary/50 focus:border-primary"
          required
          readOnly={isEditMode}
          disabled={isEditMode}
        />
        {!isEditMode && (
          <p className="mt-1 text-sm text-gray-500">
            This code will be used for login and must be unique
          </p>
        )}
      </div>

      <div>
        <label htmlFor="description" className="block text-sm font-medium text-gray-700 mb-1">
          Description
        </label>
        <textarea
          id="description"
          name="description"
          value={organizationData.description || ''}
          onChange={handleChange}
          rows={3}
          className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-primary/50 focus:border-primary"
        />
      </div>

      {/* Initial user — create mode only. Seeds the org's first account so the
          new firm has someone who can log in and manage it from day one. */}
      {!isEditMode && (
        <div className="border-t border-gray-200 pt-4 space-y-4">
          <div>
            <h3 className="text-sm font-semibold text-gray-800">Initial user</h3>
            <p className="mt-1 text-sm text-gray-500">
              The first account for this organization. They'll get a setup link to
              set their password.
            </p>
          </div>

          <div>
            <label htmlFor="initial-user-email" className="block text-sm font-medium text-gray-700 mb-1">
              Email *
            </label>
            <input
              type="email"
              id="initial-user-email"
              name="email"
              value={initialUser.email}
              onChange={handleInitialUserChange}
              className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-primary/50 focus:border-primary"
              required
            />
          </div>

          <div>
            <label htmlFor="initial-user-name" className="block text-sm font-medium text-gray-700 mb-1">
              Full Name
            </label>
            <input
              type="text"
              id="initial-user-name"
              name="name"
              value={initialUser.name || ''}
              onChange={handleInitialUserChange}
              className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-primary/50 focus:border-primary"
            />
          </div>

          <div>
            <label htmlFor="initial-user-role" className="block text-sm font-medium text-gray-700 mb-1">
              Role
            </label>
            <select
              id="initial-user-role"
              name="role"
              value={initialUser.role}
              onChange={handleInitialUserChange}
              className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-primary/50 focus:border-primary"
            >
              {INITIAL_USER_ROLES.map(r => (
                <option key={r.key} value={r.key}>{r.label}</option>
              ))}
            </select>
          </div>
        </div>
      )}

      <div className="flex justify-end space-x-2 pt-4">
        <button
          type="button"
          onClick={onCancel}
          className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
          disabled={loading}
        >
          Cancel
        </button>
        <button
          type="submit"
          className="px-4 py-2 bg-primary text-secondary rounded-md hover:bg-primary/90 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary"
          disabled={loading}
        >
          {loading ? 'Saving...' : isEditMode ? 'Update Organization' : 'Create Organization'}
        </button>
      </div>
    </form>
  );
};

export default OrganizationForm;
